import React from "react";
import { ThemeProvider } from "@mui/material/styles";
import { useMaterialGridTheme } from "../../viewGrid/materialGridTheme";
import { FormControl, FormLabel, Typography } from "@mui/joy";
import { useAuthFetch } from "../../../../../utils/useAuthFetch";
import { useViewQuery } from "../../../hooks/useViewDataQuery";
import { FileCell } from "../../viewGrid/useViewGridColumns";
import {
  isCardFieldKey,
  plainFieldPath,
} from "../../../views/canvasCardFieldKeys";
import { useCanvasRuntime } from "../CanvasRuntimeContext";
import type { FieldState, RenderableElement } from "../CanvasRenderer";

/** Uses the same persisted file reference and authorized upload as View grids. */
export function CanvasFileField({
  element,
  fieldState,
  readonly,
}: {
  element: RenderableElement;
  fieldState?: FieldState;
  readonly: boolean;
}) {
  const materialGridTheme = useMaterialGridTheme();
  const runtime = useCanvasRuntime();
  const authFetch = useAuthFetch();
  const alias = element.data_binding?.data_source_id || "main";
  const source = runtime?.dataSources?.find(
    (source) => source.name === alias || source.id === alias,
  );
  const viewId = source?.view_id ? String(source.view_id) : null;
  const { data: view } = useViewQuery(viewId);
  const fieldPath = element.data_binding?.field_path || "";
  const rowId = isCardFieldKey(fieldPath)
    ? runtime?.selectionDetail?.rowId
    : runtime?.rowId;
  const tableId = view?.focus_table_id;
  return (
    <FormControl>
      <FormLabel>{element.props.label}</FormLabel>
      {!rowId || rowId === "__new_row__" ? (
        <Typography level="body-sm">Save row first</Typography>
      ) : !runtime ||
        !viewId ||
        !tableId ||
        !element.data_binding?.field_path ? (
        <Typography level="body-sm">
          File field requires a bound View.
        </Typography>
      ) : (
        <ThemeProvider theme={materialGridTheme}>
          <FileCell
            value={fieldState?.value}
            rowId={String(rowId)}
            columnField={plainFieldPath(fieldPath)}
            focusTableId={String(tableId)}
            viewId={viewId}
            workspaceId={runtime.workspaceId}
            authFetch={authFetch}
            allowEdit={
              !readonly && runtime.canEdit === true && !!fieldState?.onChange
            }
            writeContext={
              runtime.canvasId
                ? {
                    canvasId: runtime.canvasId,
                    elementId: element.id,
                    embeddingPath: runtime.embeddingPath,
                  }
                : undefined
            }
            onUploaded={fieldState?.onChange}
            onRefresh={runtime.onRefresh}
          />
        </ThemeProvider>
      )}
    </FormControl>
  );
}
